import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IVehicleCategoryPartsFilters } from "../models/Part";
import { DefaultResponseWithData } from "../models/system";

export interface IVehicleCategoryStockTableRow {
  categoryId: string;
  categoryName: string;
  internalStock: number;
  averageDaysInStock: number;
  averagePrice: number;
  totalValue: number;
}

export interface IVehicleCategoryStockTableResponse
  extends DefaultResponseWithData<IVehicleCategoryStockTableRow[]> {}

interface IVehicleCategorySummaryFallbackRow {
  categoryId: string;
  categoryName: string;
  internalStock: number;
  lastSoldPrice: number | null;
}

interface IVehicleCategorySummaryFallbackResponse
  extends DefaultResponseWithData<IVehicleCategorySummaryFallbackRow[]> {}

export default function useVehicleCategoryStockTable(
  vehicleId: string | undefined,
  filters?: IVehicleCategoryPartsFilters
) {
  const { post } = useApiHelper();
  const staleTime = 1000 * 60 * 60; // 60 minutes

  const stockTable = useQuery({
    queryKey: ["vehicleCategoryStockTable", vehicleId, filters],
    queryFn: async () => {
      try {
        return await post<IVehicleCategoryPartsFilters, IVehicleCategoryStockTableResponse>(
          `/part/vehicle/${vehicleId}/part-categories/stock-table/search`,
          filters ?? {}
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "";

        // Supports mixed FE/BE deploys while new endpoint is rolling out.
        if (!message.includes("HTTP Error: 405")) {
          throw error;
        }

        const fallback = await post<IVehicleCategoryPartsFilters, IVehicleCategorySummaryFallbackResponse>(
          `/part/vehicle/${vehicleId}/part-categories/search`,
          filters ?? {}
        );

        return {
          ...fallback,
          data: (fallback.data ?? []).map((row) => ({
            categoryId: row.categoryId,
            categoryName: row.categoryName,
            internalStock: row.internalStock ?? 0,
            averageDaysInStock: 0,
            averagePrice: row.lastSoldPrice ?? 0,
            totalValue: (row.internalStock ?? 0) * (row.lastSoldPrice ?? 0),
          })),
        };
      }
    },
    enabled: !!vehicleId,
    staleTime,
  });

  return {
    stockTable,
  };
}
